import { ClockIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { describeApiError } from '../../data/http';
import { getMyRegionHint } from '../../data/me';
import { listAvailableTemplates } from '../../data/wizard';
import { SLIDE_LIBRARY_COUNTRIES } from '../../data/slideLibrary';
import { SUPPORTED_LANGUAGES } from '../../i18n';
import type { TemplateChoice } from '../../data/types';

export function TemplatePickerPage() {
  const { t, i18n } = useTranslation();

  const [templates, setTemplates] = useState<TemplateChoice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  // Auto-detected from the caller's Entra region, but the picker's own controls can override it.
  const [country, setCountry] = useState('');
  const [countryDefaulted, setCountryDefaulted] = useState(false);

  // null = follow the app's current language (including later switches); a string (even '') means
  // the user picked something in this dropdown themselves, which then wins over the app language.
  const [languageOverride, setLanguageOverride] = useState<string | null>(null);
  const language = languageOverride ?? i18n.resolvedLanguage ?? i18n.language;

  // Derived rather than a stored flag flipped in the effect — see requestKey below.
  const requestKey = countryDefaulted ? `${language}|${country}` : null;
  const loading = requestKey !== null && requestKey !== loadedKey;

  useEffect(() => {
    let active = true;
    void getMyRegionHint()
      .then((hint) => {
        if (active) setCountry(hint.country ?? '');
      })
      .finally(() => {
        if (active) setCountryDefaulted(true);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (requestKey === null) return;
    let active = true;
    listAvailableTemplates(language, country)
      .then((items) => {
        if (!active) return;
        setTemplates(items);
        setError(null);
      })
      .catch((err: unknown) => {
        if (active) setError(describeApiError(err, t('errors.loadTemplates')));
      })
      .finally(() => {
        if (active) setLoadedKey(requestKey);
      });
    return () => {
      active = false;
    };
  }, [requestKey, language, country, t]);

  const selectClass =
    'mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22';

  return (
    <>
      <h1 className="text-3xl font-heading font-semibold tracking-normal text-foreground">
        {t('wizard.picker.title')}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">{t('wizard.picker.subtitle')}</p>

      <div className="mt-4 grid gap-4 sm:max-w-md sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium text-foreground">{t('wizard.picker.filterCountry')}</span>
          <select value={country} onChange={(e) => setCountry(e.target.value)} className={selectClass}>
            <option value="">{t('wizard.picker.allCountries')}</option>
            {SLIDE_LIBRARY_COUNTRIES.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="font-medium text-foreground">{t('wizard.picker.filterLanguage')}</span>
          <select value={language} onChange={(e) => setLanguageOverride(e.target.value)} className={selectClass}>
            <option value="">{t('wizard.picker.allLanguages')}</option>
            {SUPPORTED_LANGUAGES.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-muted-foreground" aria-live="polite">
          {t('common.loading')}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="mt-6 border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {!loading && !error && templates.length === 0 ? (
        <section className="mt-6 rounded-lg border border-border bg-surface p-6">
          <div className="grid size-10 place-items-center rounded bg-accent text-accent-foreground">
            <DocumentTextIcon className="size-5" aria-hidden="true" />
          </div>
          <h2 className="mt-3 text-base font-semibold text-foreground">{t('wizard.picker.emptyTitle')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('wizard.picker.emptyBody')}</p>
        </section>
      ) : null}

      {!loading && !error && templates.length > 0 ? (
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {templates.map((template) => (
            <li key={template.id}>
              <Link
                to={`/app/new/${template.id}/details`}
                className="flex h-full flex-col rounded-lg border border-border bg-surface p-5 transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <span className="text-base font-semibold text-foreground">{template.name}</span>
                <span className="mt-1 flex-1 text-sm text-muted-foreground">{template.description}</span>

                <span className="mt-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <ClockIcon className="size-4" aria-hidden="true" />
                    {/* DC-13: what the same document would cost by hand. */}
                    {t('wizard.picker.estimate', { count: template.estimatedMinutes })}
                  </span>
                  <span>{t('wizard.picker.fields', { count: template.propertyCount })}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}
